import {Card, useMediaQuery, useTheme} from '@material-ui/core'
import {Dialog, DialogContent, Grid, Hidden} from '@mui/material'
import parsePhoneNumberFromString, {CountryCode} from 'libphonenumber-js'
import {first, isEmpty, values} from 'lodash'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {FormProvider, useFieldArray, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {trackEvent} from '../../../common/analytics'
import {usePersistedContact} from '../../../common/react-query/hooks/queries/usePersistedContact'
import {usePersistedOrderRequests} from '../../../common/react-query/hooks/queries/usePersistedOrderRequests'
import {useOrderIntakeStyles} from '../../../Hooks/OrderIntake/useOrderIntakeStyles'
import {useFeaturesState} from '../../../Organisms/Features'
import {useBulkCementOrderIntake} from '../../../Organisms/OrderIntake/BulkCementOrderIntake.provider'
import {generateGuid} from '../../../Organisms/OrderIntake/utils'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'

import {useSplitLoadSubmit} from './handlers/useSplitLoadSubmit'
import {PersistedMaterialsAction} from '../../components/PersistedMaterialsDialog/PersistedMaterialsBanner'
import {PersistedMaterialsDialog} from '../../components/PersistedMaterialsDialog/PersistedMaterialsDialog'
import {ShowOrderSummarySection} from '../../components/ShowOrderSummaryBtn'
import {SummaryNoValidMaterials} from '../../components/SummaryNoValidMaterials'
import {DEFAULT_CUSTOMER_REFERENCE} from '../../declarations/constants'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {
  AddingNewMaterialMetadata,
  OrderRequest,
  OrderRequestGroupResponse,
  OrdersFormValidityState
} from '../../declarations/types'
import {useIsFeatureEnabledForSelectedCustomerCountry} from '../../FeatureFlags'
import {useBulkOrderFormChangeHandlerWithMethods} from '../../FormChangeHandlers'
import {useOrderIntakeData} from '../../hooks'
import {useActivePopup} from '../../hooks/useActivePopup'
import * as options from '../../Options'
import {isInvalidOrderIntakeOption} from '../../Options/query'
import {useHandleClose} from '../hooks/useHandleClose'
import {useMaterialEditorController} from '../hooks/useMaterialEditorController'
import {
  areAllCustomerReferencesSame,
  getMaterialOptionsByInvalidity,
  mergePersistedContact,
  mergePersistedOrders
} from '../utils'
import {CollectOrderIntakeRequestConfirmation} from './CollectOrderIntakeRequestConfirmation'

import {usePostOrderRequestCollection} from '../../../common/react-query/hooks/mutations/usePostOrderRequestCollection'
import {usePostOrderRequestCollectionV2} from '../../../common/react-query/hooks/mutations/usePostOrderRequestCollectionV2'
import {OrderWindowStatus, Slot, SlotReleaseData} from '../containers/slotManagement/types'
import {useLocation} from 'react-router'
import {SplitLoadOrderIntakePayload} from '../containers/SplitLoad/buildSplitLoadPayload'
import {useSlotRelease} from '../containers/slotManagement/hooks/useSlotRelease'
import {useResetSlotSiloConfig} from '../containers/slotManagement/hooks/useResetSlotSiloConfig'
import {useCollectSubmit} from './handlers/useCollectSubmit'
import {useFieldHandlers} from '../hooks/useFieldHandlers'
import {usePersistedMaterialsState} from '../hooks/usePersistedMaterialsState'
import {CollectMainContent} from './CollectMainContent'
import {CollectSidebar} from './CollectSidebar'

export const BulkCementCollectOrderForm: React.FC = () => {
  const classes = useOrderIntakeStyles()
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const {
    customerId,
    userId,
    userPrimaryOrganisationalUnit,
    userFullName,
    userProfileCountry,
    features
  } = useOrderIntakeData()
  const openEditForm = useRef(true)
  const {getFeature} = useFeaturesState()

  const theme = useTheme()

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isSlotsManagementEnabled = getFeature('OrderIntakeSlotsCollect')
  const nextDayCollect = getFeature('OrderIntakeChangeOrderDefaultDayCollect')
  const applyMaterialEnteredDescription = getFeature('OrderIntakeMaterialEnteredDescription')
  const applyContractItemDescription = getFeature('OrderIntakeContractItemDescription')
  const showHaulierInformation =
    getFeature('OrderIntakeHaulierInformation') ||
    getFeature('OrderIntakeHaulierInformationWithoutPhoneNumber')

  const shippingType = ShippingType.COLLECT
  const isCollectSlotManagementEnabled =
    ShippingType.COLLECT === shippingType &&
    getFeature('orderIntakeSlotManagedPlantLaneAvailability')

  const {setCollections, slotConfiguration, setOrderSummary, collections} =
    useBulkCementOrderIntake()

  const enableV2Options = getFeature('EnableV2Options')

  const {
    isLoading: isLoadingV1,
    isError: isErrorV1,
    mutateAsync: mutateAsyncV1
  } = usePostOrderRequestCollection()

  const {
    isLoading: isLoadingV2,
    isError: isErrorV2,
    mutateAsync: mutateAsyncV2
  } = usePostOrderRequestCollectionV2()

  const configuration = {
    isSlotsManagementEnabled,
    nextDayCollect,
    applyMaterialEnteredDescription,
    applyContractItemDescription
  }

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const isSplitLoadMode = searchParams.get('loadType') === 'splitLoad'
  const isSplitLoadFeatureEnabled =
    useIsFeatureEnabledForSelectedCustomerCountry('OrderIntakeSplitLoad')

  const shouldUseV2 = !!enableV2Options
  const isLoading = shouldUseV2 ? isLoadingV2 : isLoadingV1
  const isError = shouldUseV2 ? isErrorV2 : isErrorV1
  const shouldUseV2ForSplitLoad = shouldUseV2 && isSplitLoadMode

  // Redirect if user tries to access split load mode when feature is disabled
  useEffect(() => {
    if (isSplitLoadMode && !isSplitLoadFeatureEnabled) {
      // Remove the loadType parameter and redirect to regular collect mode
      searchParams.delete('loadType')
      const newUrl = searchParams.toString()
        ? `${location.pathname}?${searchParams.toString()}`
        : location.pathname
      window.location.href = newUrl
    }
  }, [isSplitLoadMode, isSplitLoadFeatureEnabled, location.pathname, searchParams])

  const {data: collectOptions} = options.useCollectionOptions(configuration, customerId)
  const defaultBusinessLine = options.getBusinessLine(collectOptions)

  const siteOptions = values(collectOptions)

  const methods = useForm<BulkOrderFormValues>({
    mode: 'all',
    defaultValues: {
      selectedSite: siteOptions[0],
      orders: [],
      driverInstructions: '',
      contact: {fullName: undefined, mainPhone: undefined},
      isConfirmationMailChecked: true,
      isOrderRequestConfirmed: false
    }
  })

  const {
    fields: orderFields,
    append: appendOrder,
    remove: deleteOrder,
    replace: replaceOrders
  } = useFieldArray({
    control: methods.control,
    name: 'orders'
  })

  const {selectedSite, orders} = methods.watch()

  const siteNumber = methods.watch('selectedSite.shippingAddress.siteNumber')
  const initialOrderCustomerReference = methods.watch('orders.0.payload.customerReference')
  const selectedSplitLoadMaterialsFromParentForm = methods.watch('selectedSplitLoadMaterials')
  const savedBomKeysFromParentForm = methods.watch('savedBomKeys')

  const {data: persistedContact} = usePersistedContact(siteNumber)
  const {data: persistedOrderRequests, isFetching: isOrderRequestsCacheLoading} =
    usePersistedOrderRequests(siteNumber)
  const {data: defaultCollection} = options.useDefaultCollection(
    customerId,
    configuration,
    siteNumber
  )

  const persistedMaterials = usePersistedMaterialsState({
    selectedSite,
    orders,
    defaultOrderRequest: defaultCollection as OrderRequest,
    isSlotsManagementEnabled,
    useConfigurableSlots: false,
    slotConfiguration,
    persistedOrderRequests,
    shippingType: ShippingType.COLLECT,
    userId,
    userProfileCountry,
    userPrimaryOrganisationalUnit,
    customerId,
    siteNumber,
    defaultBusinessLine
  })

  useEffect(() => {
    setOrderSessionEventId(generateGuid())
  }, [])

  useEffect(() => {
    const resolvedSelectedSite = selectedSite ?? collectOptions?.[siteNumber]

    if (
      !resolvedSelectedSite ||
      !collectOptions ||
      !defaultCollection ||
      !userFullName ||
      isInvalidOrderIntakeOption(defaultCollection)
    )
      return

    const orderRequestBySiteNumber = orders.find((or) => or.payload.siteNumber === siteNumber)
    const orderRequests = orderRequestBySiteNumber
      ? [orderRequestBySiteNumber]
      : [defaultCollection]

    const contact = mergePersistedContact(
      resolvedSelectedSite,
      persistedContact,
      features.isContactPersistency
    )

    methods.setValue(
      'isConfirmationMailChecked',
      !first(persistedOrderRequests)?.isSendingConfirmationEmailUnChecked || true
    )

    methods.setValue(
      'isBackupPhoneVisible',
      features.hasSiteContactNameAndPhone &&
        !isEmpty(contact?.backupPhone) &&
        contact?.backupPhone !== contact?.mainPhone
    )

    formChangeHandlers.onSiteChange(siteNumber, collectOptions, defaultCollection)

    // Map order requests with customer reference and preserve dates for material selection
    // Dates will be cleared in UI if slot management is enabled
    const processedOrders = orderRequests.map((orderRequest) => ({
      ...orderRequest,
      payload: {
        ...orderRequest.payload,
        customerReference: orderRequest.payload.customerReference || DEFAULT_CUSTOMER_REFERENCE
      },
      initialFullName: userFullName
    }))

    // Clear dates AFTER mapping if slot management is enabled
    // This ensures materials are selected with valid dates first
    const finalOrders = isCollectSlotManagementEnabled
      ? processedOrders.map((order) => ({
          ...order,
          initialDeliveryDate: '' as string,
          payload: {
            ...order.payload,
            deliveryDate: '' as string,
            deliveryTime: {
              earliest: '' as string,
              latest: '' as string
            }
          }
        }))
      : processedOrders

    replaceOrders(finalOrders)

    methods.reset({
      ...methods.getValues(),
      isOrderRequestConfirmed: false,
      contact: {
        mainPhone: contact.mainPhone
          ? (parsePhoneNumberFromString(contact.mainPhone, userProfileCountry as CountryCode)
              ?.number as string)
          : '',
        backupPhone: contact.backupPhone
          ? (parsePhoneNumberFromString(contact.backupPhone, userProfileCountry as CountryCode)
              ?.number as string)
          : '',
        fullName: contact.fullName ? contact.fullName : userFullName,

        carbonCopyEmail: contact.carbonCopyEmail,
        siteContactName: features.hasSiteContactNameAndPhone ? contact.siteContactName : ''
      }
    })

    persistedMaterials.refreshValidMaterials()
  }, [
    siteNumber,
    collectOptions,
    defaultCollection,
    userFullName,
    persistedContact,
    persistedOrderRequests
  ])

  const [showConfirmationStatusScreen, setShowConfirmationStatusScreen] = useState<boolean>(false)
  const [editorActivationTimeStamp, setEditorActivationTimeStamp] = useState<string>()
  const [orderSessionEventId, setOrderSessionEventId] = useState<string>()
  const [isAddingNewMaterial, setIsAddingNewMaterial] = useState<AddingNewMaterialMetadata>({
    isAddingNewMaterial: false,
    newMaterialIndex: -1
  })
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState<number>(-1)
  const [placeOrderStatus, setPlaceOrderStatus] = useState<boolean>(false)
  const [orderSummaryDataArray, setOrderSummaryDataArray] = useState<OrderRequestGroupResponse[]>(
    []
  )
  const [splitLoadPayloads, setSplitLoadPayloads] = useState<SplitLoadOrderIntakePayload[]>([])
  const [ordersFormValidity, setOrdersFormValidity] = useState<OrdersFormValidityState[]>([])
  const [isEditOrderButtonClicked, setIsEditOrderButtonClicked] = useState<boolean>(false)

  const [orderWindowStatus, setOrderWindowStatus] = useState<OrderWindowStatus>(
    OrderWindowStatus.Unreserved
  )

  const [canShowSummaryPage, setCanShowSummaryPage] = useState<boolean>(false)

  React.useEffect(() => {
    if (
      isAddingNewMaterial.isAddingNewMaterial &&
      orderFields.length === isAddingNewMaterial.newMaterialIndex + 1
    ) {
      const index = orderFields.length - 1
      handleEditorActivation(orderFields[index].id, 'details', index)
      setIsAddingNewMaterial({isAddingNewMaterial: false, newMaterialIndex: 0})
      return
    }

    if (openEditForm.current && orderFields?.length === 1) {
      openEditForm.current = false
      editFirstMaterial()
    }
  }, [isAddingNewMaterial, orderFields])

  React.useEffect(() => {
    setSelectedMaterialIndex(0)
    materialEditorController.setChanges(false)
    openEditForm.current = true
    setOrdersFormValidity([])
  }, [selectedSite])

  const isAllOrdersValid = useMemo(
    () => ordersFormValidity.every((validity) => (validity ? !validity.isFormInvalid : false)),
    [ordersFormValidity, orderFields]
  )

  // Slot Management order window countdown
  const [endTime, setEndTime] = useState<number | null>(null)
  const [remaining, setRemaining] = useState<number>(0)
  const [slots, setSlots] = useState<Slot[]>([])

  const expired = OrderWindowStatus.Expired
  const reservation = OrderWindowStatus.Reserved

  const canShowWindowAlert =
    isCollectSlotManagementEnabled && orderWindowStatus === reservation && slots.length > 0
  const canShowExpiredWindowAlert =
    isCollectSlotManagementEnabled && orderWindowStatus === expired && slots.length > 0
  const shouldRedirectToSummaryPage =
    isCollectSlotManagementEnabled && canShowSummaryPage && slots.length > 0

  const {releaseSlot} = useSlotRelease()
  const {resetSlotSiloConfig} = useResetSlotSiloConfig()
  const handleSlotRelease = (data: SlotReleaseData, onSuccess?: () => void) => {
    releaseSlot({
      externalReference: data.externalReference,
      plantId: data.plantId,
      materialNumber: data.materialNumber,
      onSuccess
    })
  }

  useEffect(() => {
    // Only run this effect when canShowWindowAlert is true
    if (!canShowWindowAlert) return

    if (!endTime) {
      const FIFTEEN_MINUTES = 15 * 60 * 1000
      const newEndTime = Date.now() + FIFTEEN_MINUTES
      setEndTime(newEndTime)
    }

    const interval = setInterval(() => {
      if (endTime) {
        const timeDifference = Math.max(0, Math.floor((endTime - Date.now()) / 1000))
        setRemaining(timeDifference)

        if (timeDifference <= 0) {
          // reset values when countdown expires
          setOrderWindowStatus(OrderWindowStatus.Expired)
          clearInterval(interval)
          setEndTime(null)
          setCanShowSummaryPage(true)

          // Use orders from the form state
          const currentOrders = methods.watch('orders') || []
          // todo: maybe we should use a different approach for this
          currentOrders.forEach((_, index) => {
            methods.setValue(`orders.${index}.payload.deliveryDate`, '' as string)
            methods.setValue(`orders.${index}.payload.deliveryTime.earliest`, '' as string)
            methods.setValue(`orders.${index}.payload.deliveryTime.latest`, '' as string)
          })

          // Run API call to release slots
          currentOrders
            .filter((item) => item.payload.slot?.externalReference)
            .map((item, index) => {
              const data: SlotReleaseData = {
                externalReference: item.payload.slot.externalReference,
                plantId: item.payload.plantNumber,
                materialNumber: item.payload.materialNumber
              }
              return handleSlotRelease(data, () => {
                resetSlotSiloConfig(methods, index)
              })
            })
        }
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [endTime, canShowWindowAlert])

  const editFirstMaterial = () => {
    const index = 0
    handleEditorActivation(orderFields[index].id, 'details', index)
  }

  const handleClose = useHandleClose(
    orders,
    setShowConfirmationStatusScreen,
    methods.reset,
    customerId
  )

  const materialEditorController = useMaterialEditorController()
  const formChangeHandlers = useBulkOrderFormChangeHandlerWithMethods(methods)
  const [activePopup, setActivePopup] = useActivePopup(
    materialEditorController.setActiveOrderBox,
    materialEditorController.activeMaterialForm,
    isEditOrderButtonClicked
  )

  const {
    onHaulerInfoChange,
    onCustomerReferenceChange,
    onQuantityChange,
    onDriverInstructionsChange,
    onDispatchNotesChange,
    onBomKeyChange,
    onMaterialChange,
    onSplitLoadMaterialsSelected,
    handleEditorActivation,
    handleStatusChange
  } = useFieldHandlers({
    methods,
    formChangeHandlers,
    materialEditorController,
    selectedSite,
    setEditorActivationTimeStamp,
    setSelectedMaterialIndex,
    t,
    shouldRedirectToSummaryPage,
    setCanShowSummaryPage
  })

  // Split load submit handler (must be called before early return)
  // Use type guard to safely access businessLineCode
  const businessLineCode =
    defaultCollection && !isInvalidOrderIntakeOption(defaultCollection)
      ? defaultCollection.payload.businessLineCode
      : ''

  const {onSplitLoadSubmit: createSplitLoadSubmit} = useSplitLoadSubmit({
    customerId,
    defaultCollectionBusinessLineCode: businessLineCode,
    hasSiteContactNameAndPhone: features.hasSiteContactNameAndPhone,
    defaultQuantityType:
      defaultCollection && !isInvalidOrderIntakeOption(defaultCollection)
        ? defaultCollection.payload.capacity.quantityType
        : '',
    orderIntakeCustomerReferenceField: features.orderIntakeCustomerReferenceField,
    mutateAsync: mutateAsyncV1,
    setCollections,
    setPlaceOrderStatus,
    setOrderSummaryDataArray,
    setOrderSummary,
    setShowConfirmationStatusScreen,
    setSplitLoadPayloads,
    mutateAsyncV2,
    shouldUseV2: shouldUseV2ForSplitLoad,
    orgUnitId:
      selectedSite?.materials[collections[0]?.payload?.materialEnteredNumber]?.[0].material
        ?.orgUnitId ??
      userPrimaryOrganisationalUnit ??
      '',
    country: userProfileCountry ?? selectedSite?.shippingAddress.country ?? '',
    allocatorInstructions: collections[0]?.payload?.orderAllocatorInstructions,
    siteId: selectedSite?.shippingAddress.siteId ?? selectedSite?.shippingAddress.siteNumber ?? ''
  })
  const onSplitLoadSubmit = createSplitLoadSubmit(materialEditorController)

  if (
    !collectOptions ||
    !selectedSite ||
    !defaultCollection ||
    isInvalidOrderIntakeOption(defaultCollection)
  )
    return null

  const addCollection = () => {
    const currentOrdersCount = orders.length
    const lastOrderItem = orders[orders.length - 1]
    const orderItem = {
      ...lastOrderItem.payload
    }

    // Clear delivery date and time if slot management is enabled
    const newOrderPayload = isCollectSlotManagementEnabled
      ? {
          ...orderItem,
          deliveryDate: '' as string,
          deliveryTime: {
            earliest: '' as string,
            latest: '' as string
          },
          slot: {
            loadingStation: '',
            siloNumber: '',
            externalReference: ''
          },
          haulerInfo: {}
        }
      : {
          ...orderItem,
          haulerInfo: {}
        }

    const newOrder = isCollectSlotManagementEnabled
      ? {
          ...lastOrderItem,
          initialDeliveryDate: '' as string,
          payload: newOrderPayload
        }
      : {
          ...lastOrderItem,
          payload: newOrderPayload
        }

    appendOrder(newOrder)
    trackEvent('hubOrderIntakeAddOrderRequest')
    setIsAddingNewMaterial({isAddingNewMaterial: true, newMaterialIndex: currentOrdersCount})
    methods.setValue('isOrderRequestConfirmed', true)
  }

  const removeCollection = (index: number) => {
    deleteOrder(index)
    trackEvent('hubOrderIntakeRemoveOrderRequest')
    materialEditorController.setActiveOrderBox(undefined)

    // release a single reserved slot when an order is removed
    const externalReference = orders[index]?.payload.slot?.externalReference
    const plantId = orders[index]?.payload.plantNumber
    const materialNumber = orders[index]?.payload.materialNumber
    if (canShowWindowAlert && externalReference) {
      const data: SlotReleaseData = {
        externalReference,
        plantId,
        materialNumber
      }
      handleSlotRelease(data, () => {
        resetSlotSiloConfig(methods, index)
      })
    }
  }

  const quantityUom = first(
    selectedSite.materials[(defaultCollection as OrderRequest).payload.materialEnteredNumber]
  )?.truckCapacity.capacityUom

  const {onSubmit} = useCollectSubmit({
    methods,
    materialEditorController,
    hasSiteContactNameAndPhone: features?.hasSiteContactNameAndPhone,
    setCollections,
    setPlaceOrderStatus,
    shouldUseV2,
    selectedSite,
    userProfileCountry,
    businessLineCode,
    defaultBusinessLine,
    userPrimaryOrganisationalUnit,
    mutateAsyncV1,
    mutateAsyncV2,
    setOrderSummaryDataArray,
    setOrderSummary,
    setShowConfirmationStatusScreen,
    quantityType: defaultCollection.payload.capacity.quantityType,
    orderIntakeCustomerReferenceField: features.orderIntakeCustomerReferenceField,
    orders,
    language,
    orderSessionEventId
  })

  const onClosePersistedMaterialsBanner = (action: PersistedMaterialsAction) => {
    const result = persistedMaterials.onCloseBanner(action)
    if (result === 'apply') {
      applyPersistedMaterials()
    }
  }

  const onClosePersistedMaterials = (apply: boolean) => {
    const shouldApply = persistedMaterials.onCloseDialog(apply)
    if (shouldApply) {
      applyPersistedMaterials()
    }
  }

  const applyPersistedMaterials = () => {
    openEditForm.current = true

    const orderRequestBySiteNumber = orders.find((or) => or.payload.siteNumber === siteNumber)
    const validPersistedOrderRequests = persistedMaterials.getValidPersistedOrderRequests()
    const orderRequests = mergePersistedOrders(
      orderRequestBySiteNumber ? orderRequestBySiteNumber : defaultCollection,
      selectedSite,
      validPersistedOrderRequests,
      isCollectSlotManagementEnabled
    )

    if (orderRequests) {
      replaceOrders(
        orderRequests.map((orderRequest) => ({
          ...orderRequest,
          payload: {
            ...orderRequest.payload,
            customerReference: orderRequest.payload.customerReference || DEFAULT_CUSTOMER_REFERENCE
          }
        }))
      )
    }

    persistedMaterials.trackReviewPersistedMaterials()
  }

  const handleSummarySectionEditButtonClick = (index: number) => {
    handleEditorActivation(orderFields[index].id, 'details', index)
    shouldRedirectToSummaryPage && setCanShowSummaryPage(false)
  }

  const isCustomerReferenceSame = areAllCustomerReferencesSame(orders)
  const validMaterialOptions = getMaterialOptionsByInvalidity(selectedSite.materials, false)
  const invalidMaterialOptions = getMaterialOptionsByInvalidity(selectedSite.materials, true)
  const noValidMaterial = invalidMaterialOptions.length >= 1 && isEmpty(validMaterialOptions)

  // we cannot have form in form, if so, it won't work and submit button will trigger upper form
  // so when there is activeMaterialForm, we disable the form and render only div
  const isMainFormActive = !materialEditorController.activeMaterialForm
  const Form = isMainFormActive ? 'form' : 'div'

  // Determine which submit handler to use based on split load mode
  const submitHandler = isSplitLoadMode ? onSplitLoadSubmit : onSubmit

  return (
    <FormProvider {...methods}>
      <Form onSubmit={isMainFormActive ? methods.handleSubmit(submitHandler) : undefined}>
        {showConfirmationStatusScreen && (
          <CollectOrderIntakeRequestConfirmation
            businessLine={defaultBusinessLine}
            quantityType={defaultCollection.payload.capacity.quantityType}
            quantityUom={quantityUom ?? ''}
            onClose={handleClose}
            groupOrderSummaryDataArray={orderSummaryDataArray ?? []}
            splitLoadPayloads={
              isSplitLoadMode && splitLoadPayloads.length > 0 ? splitLoadPayloads : undefined
            }
            isLoading={isLoading}
            isError={isError}
          />
        )}

        {!showConfirmationStatusScreen && (
          <Grid container style={{padding: 0}}>
            <Grid item xs={12}>
              <Grid container justifyContent="space-between">
                {features.isMaterialPersistency &&
                  persistedOrderRequests &&
                  persistedMaterials.showDialog && (
                    <Dialog open={true} onClose={() => onClosePersistedMaterials(false)}>
                      <DialogContent>
                        <PersistedMaterialsDialog
                          persistedMaterials={persistedOrderRequests}
                          validMaterials={persistedMaterials.validMaterials}
                          existsValidMaterials={persistedMaterials.existsValidPersistedMaterials}
                          onClose={onClosePersistedMaterials}
                        />
                      </DialogContent>
                    </Dialog>
                  )}

                <Grid
                  container
                  className={classes.container}
                  justifyContent="space-between"
                  data-test-id="order-intake-form-container"
                  style={{padding: 0}}
                >
                  <Grid container spacing={4}>
                    <Hidden mdDown={!!activePopup}>
                      <CollectMainContent
                        methods={methods}
                        siteOptions={siteOptions}
                        collectOptions={collectOptions}
                        defaultCollection={defaultCollection}
                        formChangeHandlers={formChangeHandlers}
                        customerId={customerId}
                        applyContractItemDescription={applyContractItemDescription}
                        applyMaterialEnteredDescription={applyMaterialEnteredDescription}
                        isMaterialPersistency={features.isMaterialPersistency}
                        persistedMaterialsBanner={persistedMaterials}
                        onClosePersistedMaterialsBanner={onClosePersistedMaterialsBanner}
                        selectedSite={selectedSite}
                        orderFields={orderFields}
                        materialEditorController={materialEditorController}
                        isAllOrdersValid={isAllOrdersValid}
                        isOrderRequestsCacheLoading={isOrderRequestsCacheLoading}
                        noValidMaterial={noValidMaterial}
                        defaultBusinessLine={defaultBusinessLine}
                        initialOrderCustomerReference={initialOrderCustomerReference}
                        showHaulierInformation={showHaulierInformation}
                        canShowWindowAlert={canShowWindowAlert}
                        removeCollection={removeCollection}
                        handleEditorActivation={handleEditorActivation}
                        setIsEditOrderButtonClicked={setIsEditOrderButtonClicked}
                        isMobile={isMobile}
                        t={t}
                        classes={classes}
                        persistedOrderRequests={persistedOrderRequests}
                        remaining={remaining}
                        addCollection={addCollection}
                        ordersFormValidity={ordersFormValidity}
                        orders={orders}
                      />
                    </Hidden>
                    <Hidden smDown={!activePopup}>
                      <CollectSidebar
                        placeOrderStatus={placeOrderStatus}
                        noValidMaterial={noValidMaterial}
                        isSplitLoadMode={isSplitLoadMode}
                        isAllOrdersValid={isAllOrdersValid}
                        activePopup={activePopup}
                        setActivePopup={setActivePopup}
                        shouldRedirectToSummaryPage={shouldRedirectToSummaryPage}
                        canShowExpiredWindowAlert={canShowExpiredWindowAlert}
                        setOrderWindowStatus={setOrderWindowStatus}
                        slots={slots}
                        materialEditorController={materialEditorController}
                        selectedMaterialIndex={selectedMaterialIndex}
                        orderFields={orderFields}
                        orders={orders}
                        isCustomerReferenceSame={isCustomerReferenceSame}
                        isSlotsManagementEnabled={isSlotsManagementEnabled}
                        editorActivationTimeStamp={editorActivationTimeStamp}
                        savedBomKeysFromParentForm={savedBomKeysFromParentForm}
                        selectedSplitLoadMaterialsFromParentForm={
                          selectedSplitLoadMaterialsFromParentForm
                        }
                        ordersFormValidity={ordersFormValidity}
                        setOrdersFormValidity={setOrdersFormValidity}
                        onHaulerInfoChange={onHaulerInfoChange}
                        onCustomerReferenceChange={onCustomerReferenceChange}
                        onQuantityChange={onQuantityChange}
                        onDriverInstructionsChange={onDriverInstructionsChange}
                        onDispatchNotesChange={onDispatchNotesChange}
                        onBomKeyChange={onBomKeyChange}
                        onMaterialChange={onMaterialChange}
                        onSplitLoadMaterialsSelected={onSplitLoadMaterialsSelected}
                        handleStatusChange={handleStatusChange}
                        removeCollection={removeCollection}
                        handleSummarySectionEditButtonClick={handleSummarySectionEditButtonClick}
                        formChangeHandlers={formChangeHandlers}
                        customerId={customerId}
                        quantityType={defaultCollection.payload.capacity.quantityType}
                        classes={classes}
                        setSlots={setSlots}
                      />
                    </Hidden>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Hidden smUp smDown={!!activePopup}>
              {!noValidMaterial ? (
                <ShowOrderSummarySection
                  isMobile={isMobile}
                  onOpenOrderSummary={() => setActivePopup('OrderSummary')}
                  disabled={
                    !methods.formState.isValid ||
                    noValidMaterial ||
                    isOrderRequestsCacheLoading ||
                    !isAllOrdersValid
                  }
                />
              ) : (
                <Grid item xs={12}>
                  <Card>
                    <SummaryNoValidMaterials invalidMaterialOptions={invalidMaterialOptions} />
                  </Card>
                </Grid>
              )}
            </Hidden>
          </Grid>
        )}
      </Form>
    </FormProvider>
  )
}
